import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {toastr} from "react-redux-toastr";
import { api } from "../../../services/api/api.service";
import {ModusNewsState, INewsItem, IGetAllNewsResponse} from "./types";
import { API_ONDLL_CRUD, API_ONDLL_NEWS, } from "../../../services/api/api.urls";
import { upload } from "../../../services/upload.api";
import { COMPANY_ABBR } from "../../../common/constants/company";
import { TryCatch } from "../../../services/TryCatch";

const initialState: ModusNewsState = {
    news: [] as INewsItem[],
    newsItem: {} as INewsItem,
    isOpen: false,
    isLoading: false,
    editMode: false,
    newsItemId: 0,
    pageNum:1,
    pageSize:10,
    totalItems:0,
    totalPages:0
}

export const getNews = createAsyncThunk<IGetAllNewsResponse, string, { rejectValue: string }>(
    'news/get',
    async (queryString, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_ONDLL_NEWS}?${queryString}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to fetch news.')
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const getSingleNewsItem = createAsyncThunk<{ item: INewsItem }, number, { rejectValue: string }>(
    'news/getSingle',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_ONDLL_NEWS}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to fetch a news item.')
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const createNewsItem = createAsyncThunk<INewsItem, any, { rejectValue: string }>(
    'news/post',
    async (user, {rejectWithValue}) => {
        const result = await TryCatch(
            () => upload.post(API_ONDLL_CRUD, user).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to create a news item.')
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "News item has been created");
        return result.data;
    }
)

export const editNewsItem = createAsyncThunk<INewsItem, any, { rejectValue: string }>(
    'news/edit',
    async (newsItem, {rejectWithValue,dispatch}) => {
        const result = await TryCatch(
            () => upload.put(`${API_ONDLL_CRUD}`, newsItem.payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to edit a news item.')
            return rejectWithValue(result.error);
        }
        dispatch(formHandler(false))
        toastr.success(COMPANY_ABBR, "News item has been edited");
        return result.data;
    }
)

export const deleteNewsItem = createAsyncThunk<INewsItem, number, { rejectValue: string }>(
    'news/delete',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.delete(`${API_ONDLL_CRUD}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to delete a news item.')
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "News item has been deleted");
        return result.data;
    }
)

export const cmsModusNewsSlice = createSlice({
    name: 'cmsModusNews',
    reducers: {
        formHandler: (state, action: PayloadAction<boolean>) => {
            state.isOpen = action.payload;
        },
        setNewsItemId: (state, action: PayloadAction<number>) => {
            state.newsItemId = action.payload;
        },
        editModeHandler: (state, action: PayloadAction<boolean>) => {
            state.editMode = action.payload;
        },
        setPageNum:(state,action) => {
            state.pageNum = action.payload
        },
        setPageSize:(state,action) => {
            state.pageSize = action.payload
        },
        clearUpdatedNewsInfo: (state)=> {
            state.newsItemId = 0
            state.newsItem = {} as INewsItem
        }
    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getNews.pending, (state: ModusNewsState) => {
                state.isLoading = true;
            })
            .addCase(getNews.fulfilled, (state: ModusNewsState, action) => {
                state.isLoading = false;
                state.news = action.payload.items;
                state.totalItems = action.payload.totalItems
                state.totalPages = action.payload.totalPages
            })
            .addCase(getNews.rejected, (state: ModusNewsState) => {
                state.isLoading = false;
            })
            .addCase(getSingleNewsItem.fulfilled, (state: ModusNewsState, action) => {
                state.newsItem = action.payload.item;
                state.editMode = true;
            })
            .addCase(createNewsItem.pending, (state: ModusNewsState) => {
                state.isOpen = false;
                state.isLoading = true;
            })
            .addCase(createNewsItem.fulfilled, (state: ModusNewsState, action) => {
                state.news = state.news.concat(action.payload);
                state.isLoading = false;
            })
            .addCase(createNewsItem.rejected, (state: ModusNewsState) => {
                state.isLoading = false
            })
            .addCase(editNewsItem.pending, (state: ModusNewsState) => {
                state.editMode = false;
                state.isLoading = true;
            })
            .addCase(editNewsItem.fulfilled, (state: ModusNewsState, action) => {
                state.isLoading = false;
                state.news = state.news.map((newsItem) => {
                    if (newsItem.id === action.payload.id) {
                        newsItem = action.payload
                    }
                    return newsItem
                })
                state.newsItemId = 0
            })
            .addCase(editNewsItem.rejected, (state: ModusNewsState) => {
                state.isLoading = false;
            })
            .addCase(deleteNewsItem.pending, (state: ModusNewsState) => {
                state.isLoading = true;
            })
            .addCase(deleteNewsItem.fulfilled, (state: ModusNewsState, action) => {
                state.news = state.news.filter((newsItem) => {
                    return newsItem.id !== action.meta.arg
                })
                state.isLoading = false;
            })
            .addCase(deleteNewsItem.rejected, (state: ModusNewsState) => {
                state.isLoading = false;
            })
    },
});

export const {
    formHandler,
    setNewsItemId,
    editModeHandler,
    setPageNum,
    setPageSize,
    clearUpdatedNewsInfo
} = cmsModusNewsSlice.actions

export default cmsModusNewsSlice.reducer;
